import { v4 as uuid } from 'uuid';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { Side } from '@/schemas/tiles';
import { isOwnedScalarTile } from '@/utils/OwnedBundleVisualData';

/** Exact experiment identity atoms, including packed scalar-selector paths. */
export function containsChipMemberIdentity(id: string): boolean {
  if (id.startsWith('adapter:[')) return true;
  if (!id.startsWith('[')) return false;
  try {
    const atoms: unknown = JSON.parse(id);
    return (
      Array.isArray(atoms) &&
      atoms.some((atom) => typeof atom === 'string' && atom.startsWith('adapter:['))
    );
  } catch {
    return false;
  }
}

type SignalIdentity = { signalIds: string[]; signalLabels: Record<string, string> };

function labelAllocator(tiles: TileInstance[], reservedLabels: string[] = []) {
  const labels = new Set([
    ...reservedLabels,
    ...tiles.flatMap((tile) => Object.values(tile.meta.bundle?.signalLabels ?? {})),
  ]);
  let next = 1;
  for (const label of labels) {
    const number = /^S([1-9]\d*)$/.exec(label);
    const value = number && Number(number[1]);
    if (value && Number.isSafeInteger(value) && value <= 1_000_000_000)
      next = Math.max(next, value + 1);
  }
  return () => {
    while (labels.has(`S${next}`)) next++;
    const label = `S${next++}`;
    labels.add(label);
    return label;
  };
}

/** New paths receive identities once; position and later endpoint permutations never name signals. */
export function createBundleSignalIdentity(
  width: number,
  tiles: TileInstance[],
  reservedLabels: string[] = [],
): SignalIdentity {
  const nextLabel = labelAllocator(tiles, reservedLabels);
  const pathId = uuid();
  const signalIds = Array.from(
    { length: width },
    (_, lane) => `signal:${pathId}:${lane}`,
  );
  return {
    signalIds,
    signalLabels: Object.fromEntries(signalIds.map((id) => [id, nextLabel()])),
  };
}

/** Add persistent identities to legacy bundles without moving cells or changing their wiring. */
export function ensureBundleSignalIdentities(tiles: TileInstance[]): void {
  // Scalar-only native circuits need no label/origin work. Unknown metadata and
  // custom read hooks retain the full ordered normalization path below.
  if (tiles.every(isOwnedScalarTile)) return;
  const nextLabel = labelAllocator(tiles);
  const knownLabels = new Map<string, string>();
  for (const tile of tiles)
    for (const [id, label] of Object.entries(tile.meta.bundle?.signalLabels ?? {}))
      if (!knownLabels.has(id)) knownLabels.set(id, label);
  for (const tile of tiles) {
    const bundle = tile.meta.bundle;
    if (
      !bundle ||
      !Number.isSafeInteger(bundle.width) ||
      bundle.width < 1 ||
      bundle.width > 4096
    )
      continue;
    if (!bundle.signalIds)
      bundle.signalIds = Array.from(
        { length: bundle.width },
        (_, lane) => `signal:${tile.id}:${lane}`,
      );
    // Existing malformed identities remain diagnostic data rather than silently repaired wiring.
    if (bundle.signalIds.length !== bundle.width) continue;
    const labels = new Map(Object.entries(bundle.signalLabels ?? {}));
    let changed = !bundle.signalLabels;
    for (const id of bundle.signalIds) {
      if (!labels.has(id)) {
        const label = knownLabels.get(id) ?? nextLabel();
        labels.set(id, label);
        knownLabels.set(id, label);
        changed = true;
      }
    }
    if (changed) bundle.signalLabels = Object.fromEntries(labels);
  }
  const byId = new Map(tiles.map((tile) => [tile.id as string, tile]));
  const bundles = tiles
    .filter(
      (tile) =>
        tile.meta.bundle?.signalIds &&
        tile.meta.bundle.signalIds.length === tile.meta.bundle.width,
    )
    .sort(
      (a, b) =>
        b.meta.bundle!.width - a.meta.bundle!.width ||
        a.location.y - b.location.y ||
        a.location.x - b.location.x ||
        a.id.localeCompare(b.id),
    );
  const owners = new Map<string, TileInstance>();
  const groups = new Map<string, TileInstance[]>();
  const groupKey = (ids: string[]) => JSON.stringify([...ids].sort());
  for (const tile of bundles) {
    const key = groupKey(tile.meta.bundle!.signalIds!);
    const group = groups.get(key) ?? [];
    group.push(tile);
    groups.set(key, group);
  }
  const includes = (owner: TileInstance, ids: string[]) =>
    ids.every((id) => owner.meta.bundle?.signalIds?.includes(id));
  for (const tile of bundles) {
    const bundle = tile.meta.bundle!;
    const ids = bundle.signalIds!;
    const existing = bundle.signalOrigin && byId.get(bundle.signalOrigin);
    if (existing && includes(existing, ids)) {
      for (const id of existing.meta.bundle!.signalIds!) owners.set(id, existing);
      continue;
    }
    const inherited = owners.get(ids[0]);
    if (inherited && includes(inherited, ids)) {
      bundle.signalOrigin = inherited.id;
      continue;
    }
    const siblings = groups.get(groupKey(ids))!;
    let owner: TileInstance | undefined;
    let reordered: string[] | undefined;
    if (bundle.signalOrigin) {
      // Transfer a deleted origin using its last physical identity map, never a guessed slot.
      for (const candidate of siblings) {
        for (const side of Object.values(Side)) {
          if (candidate.meta.bundle!.sides?.[side] !== undefined) continue;
          const aliases = candidate.laneSignalIds?.[side];
          if (aliases?.length !== ids.length) continue;
          const order = aliases.map((aliases) =>
            aliases.filter((id) => ids.includes(id)),
          );
          if (
            order.every((matches) => matches.length === 1) &&
            new Set(order.map((matches) => matches[0])).size === ids.length
          ) {
            owner = candidate;
            reordered = order.map((matches) => matches[0]);
            break;
          }
        }
        if (owner) break;
      }
      if (!owner) continue;
    } else owner = siblings[0];
    if (!owner) continue;
    if (reordered) owner.meta.bundle!.signalIds = reordered;
    for (const sibling of siblings) sibling.meta.bundle!.signalOrigin = owner.id;
    for (const id of ids) owners.set(id, owner);
  }
}
